import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Popconfirm, Spin } from "antd";
import { AppButton, AppInput } from "components/antd-ui";
import { StatusCell } from "components/table";
import clsx from "clsx";
import { memo, useCallback, type ChangeEvent, type MouseEvent } from "react";
import { Scenario } from "types/scenario";
import { formatDateTime } from "utils/date";
import { SCENARIO_STATUS_TRANS } from "../constants";
import { getNodeCount } from "../utils";
import "./ScenarioList.scss";

type ScenarioRowProps = {
  scenario: Scenario;
  isActive: boolean;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
};

const ScenarioRow = memo(({ scenario, isActive, onSelect, onDelete }: ScenarioRowProps) => {
  const handleClick = useCallback(() => {
    onSelect(scenario.id);
  }, [onSelect, scenario.id]);

  const handleDeleteClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    onDelete(scenario.id);
  }, [onDelete, scenario.id]);

  const nodeCount = getNodeCount(scenario.nodeCount, scenario.nodes);

  return (
    <div className={clsx("scenarioRow", isActive && "scenarioRowActive")}>
      <button type="button" className="scenarioRowMain" onClick={handleClick}>
        <div className="scenarioRowTitle">
          <span>{scenario.name}</span>
          <StatusCell value={scenario.status} trans={SCENARIO_STATUS_TRANS} />
        </div>
        <div className="scenarioRowSub">
          {nodeCount} nodes · {formatDateTime(scenario.updatedAt)}
        </div>
      </button>

      <Popconfirm
        title="Xác nhận xóa?"
        okText="Xóa"
        cancelText="Không"
        onConfirm={handleDeleteConfirm}
      >
        <button
          type="button"
          className="scenarioRowDelete"
          aria-label="Xóa tình huống"
          onClick={handleDeleteClick}
        >
          <DeleteOutlined />
        </button>
      </Popconfirm>
    </div>
  );
});

ScenarioRow.displayName = "ScenarioRow";

type ScenarioListProps = {
  scenarios: Scenario[];
  selectedId: number | null;
  search: string;
  total: number;
  loading?: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (id: number) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
};

export const ScenarioList = memo(
  ({
    scenarios,
    selectedId,
    search,
    total,
    loading,
    onSearchChange,
    onSelect,
    onAdd,
    onDelete,
  }: ScenarioListProps) => {
    const handleSearchChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onSearchChange(event.target.value);
      },
      [onSearchChange],
    );

    return (
      <section className="scenarioList">
        <div className="scenarioPanelHead">
          <h2>
            Tình huống <span className="scenarioListCount">({total})</span>
          </h2>
          <AppButton size="small" icon={<PlusOutlined />} onClick={onAdd}>
            Thêm
          </AppButton>
        </div>

        <div className="scenarioListSearch">
          <AppInput
            placeholder="Tìm theo tên..."
            value={search}
            onChange={handleSearchChange}
            allowClear
          />
        </div>

        <div className="scenarioListBody">
          {loading && !scenarios.length ? (
            <div className="scenarioListLoading">
              <Spin size="small" />
            </div>
          ) : (
            scenarios.map((scenario) => (
              <ScenarioRow
                key={scenario.id}
                scenario={scenario}
                isActive={scenario.id === selectedId}
                onSelect={onSelect}
                onDelete={onDelete}
              />
            ))
          )}
        </div>

        <div className="scenarioListFooter">Chọn một tình huống để chỉnh sửa luồng.</div>
      </section>
    );
  },
);

ScenarioList.displayName = "ScenarioList";
